import { createChatInputContainer } from './chat-input.js';
import { storage } from '../../utils/storage.js';
import { storageManager } from '../../../core/storage/index.js';
import { initializeHangoutAnalytics, getAnalyticsTracker } from '../../analytics/hangout-obs-tracker.js';
import { HangoutState, HANGOUT_EVENTS } from '../state/HangoutState.js';
import { HangoutService } from '../services/HangoutService.js';
import { withNetworkRetry, withGracefulFailure, handleInitializationFailure } from '../../utils/error-handling.js';
import { normalizeSiteLanguageCode } from '../../utils/languageData.js';
import { fullscreenManager } from '../../utils/fullscreen-manager.js';

import {
    handleHelpInspireButtonClick,
    handleFloatingButtonClick,
    handleMicSendButtonClick,
    handleChatInputKeypress,
    handleSettingsButtonClick
} from './input-handlers.js';
import { dispatchResume } from './resume-handlers.js';
import { hydrateChatSummaryFromDb, hydrateUserResponseStyleFromDb } from './hangout-settings-persist.js';
import { initOrientationChangeListener } from './mobile-handlers.js';

import {
    characterManager,
    fetchCharacterSprite
} from './character-manager.js';
import {
    sessionManager,
    initHangoutFromUrl,
    applyInitialBackground,
    initializeResumedVisuals
} from './session-manager.js';
import { USER_WORKER_URL } from '../../config.js';
import { tokenManager } from '../../utils/TokenManager.js';
import { createLanguageDropdown, getLanguageFlag } from '../../../components/navigation/language.js';
import { ensureFlagsSpriteLoaded, createFlagElement } from '../../../components/navigation/flags.js';
import { ensureSessionLayoutMode, getCurrentLayoutMode, getNextLayoutMode, LAYOUT_MODES, setCurrentLayoutMode } from './layout-mode.js';
import { getAllVoices, pickDefaultElevenLabsVoice } from '../../services/voiceService.js';
import { normalizeGender } from '../../utils/genderUtils.js';
import { toFullSizeSpriteUrl } from '../../utils/spriteFallbacks.js';
import { initializeImageZoom } from './imageZoom.js';
import { createHangoutCharacterData } from '../../utils/characterUtils.js';
import { resolveLatestCharacterSpriteData } from '../shared/sprite-state.js';
import { getGlobalHistoryMessages } from '../shared/history-resolution.js';
import {
    resolveScenarioBackdrop,
    shouldPreserveLocalBackdrop,
    shouldSeedScenarioBackdrop
} from '../shared/session-backdrop.js';
import { timeProgressionService } from '../services/TimeProgressionService.js';

const safeUpdateConfig = async (updates) => {
    const currentConfig = await storageManager.getItem('currentHangoutConfig') || {};
    Object.assign(currentConfig, updates);
    await storageManager.setItem('currentHangoutConfig', currentConfig);
    return currentConfig;
};

const HANGOUT_LANGUAGE_CONTROL_ID = 'hangout-language-control';
let languageControlCleanup = null;
let isGreetingRefreshInFlight = false;
let isUserMessageSubmittedInSession = false;
let isLayoutToggleInitialized = false;

const LAYOUT_TOGGLE_NEXT = {
    [LAYOUT_MODES.CHATBOT]: {
        title: 'Switch to VN layout',
        icon: 'fas fa-image'
    },
    [LAYOUT_MODES.VN]: {
        title: 'Hide interface',
        icon: 'fas fa-eye-slash'
    },
    [LAYOUT_MODES.NONE]: {
        title: 'Switch to chatbot layout',
        icon: 'fas fa-comments'
    }
};

const updateLayoutToggleButton = (mode) => {
    const button = document.getElementById('hangout-layout-toggle-button');
    if (!button) return;

    const icon = button.querySelector('i');
    const next = LAYOUT_TOGGLE_NEXT[mode] || LAYOUT_TOGGLE_NEXT[LAYOUT_MODES.CHATBOT];
    button.setAttribute('aria-label', next.title);
    button.setAttribute('title', next.title);
    button.dataset.layoutMode = mode;
    if (icon) {
        icon.className = next.icon;
    }
};

const initializeLayoutToggleControl = async () => {
    const button = document.getElementById('hangout-layout-toggle-button');
    if (!button) return;

    updateLayoutToggleButton(getCurrentLayoutMode());
    if (isLayoutToggleInitialized) return;

    const applyAndRenderLayout = async (nextMode) => {
        const mode = setCurrentLayoutMode(nextMode);
        const { getDisplayManager } = await import('./message-utils.js');
        const displayManager = await getDisplayManager();
        await displayManager.setLayoutMode?.(mode);
        updateLayoutToggleButton(mode);
    };

    const handleToggle = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        const nextMode = getNextLayoutMode(getCurrentLayoutMode());
        await applyAndRenderLayout(nextMode);
    };

    button.addEventListener('click', handleToggle);
    button.addEventListener('keydown', async (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        await handleToggle(event);
    });

    isLayoutToggleInitialized = true;
};

let isFullscreenControlInitialized = false;

const updateHangoutFullscreenButton = () => {
    const button = document.getElementById('hangout-fullscreen-button');
    const isFullscreen = Boolean(
        document.fullscreenElement
        || document.webkitFullscreenElement
        || document.msFullscreenElement
    );
    fullscreenManager.isFullscreenMode = isFullscreen;
    document.body.classList.toggle('fullscreen-mode', isFullscreen);

    if (!button) return;

    const icon = button.querySelector('i');
    const title = isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen';
    button.setAttribute('aria-label', title);
    button.setAttribute('title', title);
    button.setAttribute('aria-pressed', String(isFullscreen));
    if (icon) {
        icon.className = isFullscreen ? 'fas fa-compress' : 'fas fa-expand';
    }
};

const initializeFullscreenControl = () => {
    const button = document.getElementById('hangout-fullscreen-button');
    if (!button) return;

    updateHangoutFullscreenButton();
    if (isFullscreenControlInitialized) return;

    const handleToggle = async (event) => {
        event.preventDefault();
        event.stopPropagation();
        try {
            await fullscreenManager.toggleFullscreen();
        } catch (error) {
            console.warn('[Hangout] Failed to toggle fullscreen:', error);
        } finally {
            updateHangoutFullscreenButton();
        }
    };

    button.addEventListener('click', handleToggle);
    button.addEventListener('keydown', async (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        await handleToggle(event);
    });
    document.addEventListener('fullscreenchange', updateHangoutFullscreenButton);
    document.addEventListener('webkitfullscreenchange', updateHangoutFullscreenButton);
    isFullscreenControlInitialized = true;
};

const normalizeDisplayLanguage = (lang) => normalizeSiteLanguageCode(lang);

const hasUserMessageInState = (messages) =>
    Array.isArray(messages) && messages.some((message) => message?.role === 'user');

const updateLanguageButtonVisuals = (controlElement, language) => {
    const langDropdownButton = controlElement.querySelector('.lang-dropbtn');
    if (!langDropdownButton) return;

    const existingFlag = langDropdownButton.querySelector('svg.flag-icon');
    const replacementFlag = createFlagElement(getLanguageFlag(language));
    if (existingFlag) {
        existingFlag.replaceWith(replacementFlag);
    } else {
        langDropdownButton.prepend(replacementFlag);
    }

    const codeElement = langDropdownButton.querySelector('.lang-code');
    if (codeElement) {
        codeElement.textContent = language.toUpperCase();
    }
};

const persistUserLanguagePreference = async (language) => {
    const userData = storage.get('userData');
    if (!userData?.isLoggedIn) {
        return;
    }

    const token = await tokenManager.getValidAccessToken();
    if (!token) {
        return;
    }

    try {
        const response = await fetch(`${USER_WORKER_URL}/language`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ language })
        });

        if (!response.ok) {
            const errorBody = await response.text().catch(() => '');
            console.warn('[Hangout] Failed to persist language preference:', response.status, errorBody);
            return;
        }

        storage.updateJson('userData', 'language', language);
    } catch (error) {
        console.warn('[Hangout] Failed to persist language preference:', error);
    }
};

const syncLanguageControlVisibility = async (hangoutState, controlElement) => {
    if (!controlElement) return true;

    let hasUserMessages = isUserMessageSubmittedInSession || hasUserMessageInState(hangoutState.getMessages());
    if (!hasUserMessages) {
        const { hasAnyUserMessageInHangout } = await import('./message-utils.js');
        hasUserMessages = hasAnyUserMessageInHangout();
    }

    controlElement.classList.toggle('is-hidden', hasUserMessages);
    return hasUserMessages;
};

const refreshGreetingForLanguageSwitch = async (hangoutState) => {
    if (isGreetingRefreshInFlight) {
        return;
    }

    isGreetingRefreshInFlight = true;
    try {
        const { prepareGreetingReplacementForLanguageSwitch } = await import('./message-utils.js');
        const prepResult = await prepareGreetingReplacementForLanguageSwitch({
            triggerSource: 'language-control'
        });
        if (prepResult?.skipped) {
            return;
        }

        const currentConfig = await storageManager.getItem('currentHangoutConfig') || {};
        const scenarioId = currentConfig.scenarioId || hangoutState.getCurrentSession()?.scenarioId;
        const characters = hangoutState.getCharacters?.() || currentConfig.allCharacters || [];

        if (!scenarioId || !Array.isArray(characters) || characters.length === 0) {
            console.warn('[Hangout] Skipping greeting refresh after language switch: missing scenario or characters');
            return;
        }

        await sessionManager.generateInitialGreeting(
            hangoutState.getConversationId(),
            scenarioId,
            characters,
            currentConfig
        );
    } finally {
        isGreetingRefreshInFlight = false;
    }
};

const initializeLanguageControl = async (hangoutState) => {
    const controlElement = document.getElementById(HANGOUT_LANGUAGE_CONTROL_ID);
    if (!controlElement) {
        return;
    }

    if (typeof languageControlCleanup === 'function') {
        languageControlCleanup();
    }

    ensureFlagsSpriteLoaded();

    const selectedLanguage = normalizeDisplayLanguage(storage.get('displayLanguage') || 'en');
    const languageDropdown = createLanguageDropdown({ language: selectedLanguage });
    controlElement.replaceChildren(languageDropdown);

    const dropdownButton = controlElement.querySelector('.lang-dropbtn');
    const dropdownContent = controlElement.querySelector('.dropdown-content');

    if (!dropdownButton || !dropdownContent) {
        return;
    }

    const hideControlInitially = await syncLanguageControlVisibility(hangoutState, controlElement);
    isUserMessageSubmittedInSession = hideControlInitially;
    updateLanguageButtonVisuals(controlElement, selectedLanguage);

    const closeDropdown = () => {
        dropdownContent.classList.remove('show');
    };

    const handleDocumentClick = (event) => {
        if (!controlElement.contains(event.target)) {
            closeDropdown();
        }
    };

    const handleToggleDropdown = (event) => {
        event.preventDefault();
        event.stopPropagation();
        dropdownContent.classList.toggle('show');
    };

    const handleLanguageOptionClick = async (event) => {
        event.preventDefault();
        event.stopPropagation();

        const langOption = event.currentTarget;
        const selectedLang = normalizeDisplayLanguage(langOption.dataset.lang || 'en');
        const currentLang = normalizeDisplayLanguage(storage.get('displayLanguage') || 'en');

        closeDropdown();
        if (selectedLang === currentLang) {
            return;
        }

        storage.set('displayLanguage', selectedLang);
        storage.updateJson('userData', 'language', selectedLang);
        updateLanguageButtonVisuals(controlElement, selectedLang);
        document.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: selectedLang } }));
        await persistUserLanguagePreference(selectedLang);

        const hasUserMessages = await syncLanguageControlVisibility(hangoutState, controlElement);
        if (!hasUserMessages) {
            await refreshGreetingForLanguageSwitch(hangoutState);
        }
    };

    const handleLanguageChanged = async (event) => {
        const updatedLanguage = normalizeDisplayLanguage(event.detail?.language || storage.get('displayLanguage') || 'en');
        updateLanguageButtonVisuals(controlElement, updatedLanguage);
        await syncLanguageControlVisibility(hangoutState, controlElement);
    };

    const handleUserMessageSubmitted = async () => {
        isUserMessageSubmittedInSession = true;
        await syncLanguageControlVisibility(hangoutState, controlElement);
    };

    const handleMessageSent = async (payload) => {
        if (payload?.message?.role !== 'user') {
            return;
        }
        isUserMessageSubmittedInSession = true;
        await syncLanguageControlVisibility(hangoutState, controlElement);
    };

    const handleStateChanged = async () => {
        await syncLanguageControlVisibility(hangoutState, controlElement);
    };

    dropdownButton.addEventListener('click', handleToggleDropdown);
    document.addEventListener('click', handleDocumentClick);
    document.addEventListener('languageChanged', handleLanguageChanged);
    document.addEventListener('hangout:user-message-submitted', handleUserMessageSubmitted);
    const unsubscribeMessageSent = hangoutState.subscribe(HANGOUT_EVENTS.MESSAGE_SENT, handleMessageSent);
    const unsubscribeStateChanged = hangoutState.subscribe(HANGOUT_EVENTS.STATE_CHANGED, handleStateChanged);

    dropdownContent.querySelectorAll('button[data-lang]').forEach((optionButton) => {
        optionButton.addEventListener('click', handleLanguageOptionClick);
    });

    languageControlCleanup = () => {
        dropdownButton.removeEventListener('click', handleToggleDropdown);
        document.removeEventListener('click', handleDocumentClick);
        document.removeEventListener('languageChanged', handleLanguageChanged);
        document.removeEventListener('hangout:user-message-submitted', handleUserMessageSubmitted);
        if (typeof unsubscribeMessageSent === 'function') {
            unsubscribeMessageSent();
        } else {
            hangoutState.unsubscribe(HANGOUT_EVENTS.MESSAGE_SENT, handleMessageSent);
        }
        if (typeof unsubscribeStateChanged === 'function') {
            unsubscribeStateChanged();
        } else {
            hangoutState.unsubscribe(HANGOUT_EVENTS.STATE_CHANGED, handleStateChanged);
        }
        dropdownContent.querySelectorAll('button[data-lang]').forEach((optionButton) => {
            optionButton.removeEventListener('click', handleLanguageOptionClick);
        });
    };
};

// Helper function to refresh background when location changes
const refreshBackgroundAfterLocationChange = async () => {
    try {
        const { getBackgroundService } = await import('../../../core/services/BackgroundService.js');
        const backgroundService = getBackgroundService();

        // Invalidate cache to force fresh background fetch
        backgroundService._invalidateCache();

        // Get the updated background
        const currentBackground = await backgroundService.getCurrentBackground();
        if (!currentBackground.backgroundUrl) return;

        const resolvedUrl = await backgroundService.resolveExistingBackgroundFromUrl(currentBackground.backgroundUrl);
        const backgroundCanvas = document.getElementById('background-canvas');
        if (!backgroundCanvas) return;
        Object.assign(backgroundCanvas.style, {
            backgroundImage: `url(${resolvedUrl})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat'
        });
    } catch (error) {
        console.error('Failed to refresh background after location change:', error);
    }
};

async function initializeChatInputListeners() {
    const micSendButton = document.getElementById('mic-send-button');
    if (!micSendButton) {
        console.warn('initializeChatInputListeners: mic-send-button not found');
    } else {
        micSendButton.addEventListener('click', handleMicSendButtonClick);
    }

    // Attach handler to floating button
    const micSendButtonFloating = document.getElementById('mic-send-button-floating');
    if (micSendButtonFloating) {
        micSendButtonFloating.addEventListener('click', handleFloatingButtonClick);
    }

    // Help/inspiration button - placeholder for future functionality
    const helpInspireButton = document.getElementById('help-inspire-button');
    if (helpInspireButton) {
        helpInspireButton.addEventListener('click', handleHelpInspireButtonClick);
    }

    const chatInput = document.getElementById('chat-input');
    if (!chatInput) {
        console.warn('initializeChatInputListeners: chat-input element not found during initialization');
    } else {
        chatInput.addEventListener('keypress', handleChatInputKeypress);
        chatInput.addEventListener('keydown', (e) => characterManager.handleMentionDeletion(e));
        chatInput.addEventListener('input', async () => {
            characterManager.updateMentionHighlights();
        });
        await characterManager.updateMentionHighlights();

    }

    const settingsBtn = document.getElementById('settings-btn');
    if (!settingsBtn) {
        console.warn('initializeChatInputListeners: settings-btn not found');
    } else {
        settingsBtn.addEventListener('click', handleSettingsButtonClick);
    }

}

export async function initializeUI() {
    const initialLayoutMode = ensureSessionLayoutMode();
    updateLayoutToggleButton(initialLayoutMode);

    // Initialize message display manager FIRST (VN textbox)
    try {
        const { getDisplayManager } = await import('./message-utils.js');
        const displayManager = await getDisplayManager();
        await displayManager.setLayoutMode?.(initialLayoutMode);
        await displayManager.initialize();
    } catch (error) {
        console.warn('[Hangout] Failed to initialize message display manager:', error);
        // Fallback path: if VN initialization fails, restore the classic composer.
        const chatInputContainer = document.querySelector('.chat-input-container');
        if (chatInputContainer) {
            chatInputContainer.classList.remove('chat-hidden-force');
            chatInputContainer.style.display = '';
            chatInputContainer.style.visibility = '';
            chatInputContainer.style.pointerEvents = '';
        }

        const mobileFloatingControls = document.getElementById('mobile-floating-controls');
        if (mobileFloatingControls) {
            mobileFloatingControls.style.display = '';
        }
    }

    await createChatInputContainer(initializeChatInputListeners);
    await initializeLayoutToggleControl();
    initializeFullscreenControl();
    try {
        initializeImageZoom();
    } catch (error) {
        console.warn('[Hangout] Image zoom initialization failed:', error);
    }

    // Setup global modal handlers for close buttons and backdrop clicks
    document.addEventListener('click', function (e) {
        if (e.target && (/** @type {HTMLElement} */ (e.target)).classList.contains('close-modal')) {
            import('./modals/modal-utils.js').then(({ closeModal }) => closeModal());
        }
        if (e.target && (/** @type {HTMLElement} */ (e.target)).classList.contains('modal')) {
            import('./modals/modal-utils.js').then(({ closeModal }) => closeModal());
        }
    });

    let spotDisplay = document.getElementById('spot-display');
    if (!spotDisplay) {
        spotDisplay = document.createElement('div');
        spotDisplay.id = 'spot-display';
        document.body.appendChild(spotDisplay);
    }
    if (!spotDisplay.querySelector('.labels-container')) {
        const labelsContainer = document.createElement('div');
        labelsContainer.className = 'labels-container';
        spotDisplay.appendChild(labelsContainer);
    }

    const { getLocationTransitionManager } = await import('./location-transition.js');
    const transitionManager = getLocationTransitionManager();
    await withGracefulFailure(() => transitionManager.initialize(), 'Initialize location transition manager');

    await withGracefulFailure(async () => {
        await timeProgressionService._checkAndRestoreState();
        if (!timeProgressionService.isRunning) {
            await timeProgressionService.start();
        }
    }, 'Initialize TimeProgressionService');

}

const extractOutfitId = (outfitData) => {
    if (typeof outfitData === 'object' && outfitData !== null) {
        return String(outfitData.outfitId || outfitData.id || outfitData);
    }
    return String(outfitData);
};

const getCurrentSpriteState = (hangoutState, characterId) => {
    const { spriteState } = resolveLatestCharacterSpriteData({
        messages: hangoutState.getMessages() || [],
        characterId
    });
    return spriteState;
};

let eventListenersAttached = false;
let hangoutInitializeStarted = false;

function setupEventListeners() {
    if (eventListenersAttached) return;
    eventListenersAttached = true;
    initOrientationChangeListener();

    // Manual pacing is advanced via the VN UI (right arrow), not via tap-to-continue.

    const hangoutState = HangoutState.getInstance();
    hangoutState.subscribe(HANGOUT_EVENTS.OUTFIT_SELECTED, async (outfitData) => {
        const outfitId = extractOutfitId(outfitData);
        const session = hangoutState.getCurrentSession();

        if (!session?.characterId) return;

        await withGracefulFailure(async () => {
            // Skip sprite loading for new conversations - let AI response determine emotion
            const messages = hangoutState.getMessages();
            if (!messages || messages.length === 0) {
                return;
            }

            const currentSpriteState = getCurrentSpriteState(hangoutState, session.characterId);
            const { parseSpriteState } = await import('../../../core/utils/hangoutUtils.js');
            const parsedState = parseSpriteState(currentSpriteState);
            const latestSprite = resolveLatestCharacterSpriteData({
                messages,
                characterId: session.characterId
            });
            const isVideo = latestSprite.isVideo === true;

            await fetchCharacterSprite(
                session.characterId,
                parsedState.action,
                outfitId,
                0,
                true,
                isVideo
            );
        }, 'Update sprite on outfit change');
    });

    // Update sprite with last message emotion for resumed conversations ONLY
    hangoutState.subscribe(HANGOUT_EVENTS.SESSION_STARTED, async (session) => {
        if (session && session.type === 'conversation' && session.characterId) {
            await withGracefulFailure(async () => {
                const messages = hangoutState.getMessages();

                // Skip sprite loading for new conversations (no messages yet)
                if (!messages || messages.length === 0) {
                    return;
                }

                const latestSprite = resolveLatestCharacterSpriteData({
                    messages,
                    characterId: session.characterId
                });
                const lastSpriteState = latestSprite.spriteState;
                const isVideo = latestSprite.isVideo === true;

                const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
                const character = hangoutConfig?.allCharacters?.find(c => String(c.id) === String(session.characterId)) ||
                    {};

                const { parseSpriteState } = await import('../../../core/utils/hangoutUtils.js');
                const parsedState = parseSpriteState(lastSpriteState);

                await fetchCharacterSprite(
                    session.characterId,
                    parsedState.action,
                    session.outfitId || 0,
                    0,
                    true,
                    isVideo
                );
            }, 'Update sprite for resumed conversation');
        }
    });

}

const clearHangoutStateMessages = (hangoutState) => {
    hangoutState.setMessages([]);
    if (hangoutState.state) {
        hangoutState.state.messageHistory = [];
    }
};

const setupPerformanceOptimizations = (hangoutState) => {
    let navigationRenderDebounce = null;
    let backgroundServiceCallCount = 0;
    const originalNavigationRender = (/** @type {any} */ (window)).navigation?.render;

    // Suppress Navigation event subscriptions during initialization
    let suppressNavigationEvents = true;
    const originalNavigationSubscribe = (/** @type {any} */ (window)).Navigation?.prototype?._subscribeToHangoutEvents;

    if ((/** @type {any} */ (window)).Navigation?.prototype && originalNavigationSubscribe) {
        (/** @type {any} */ (window)).Navigation.prototype._subscribeToHangoutEvents = function () {
            if (suppressNavigationEvents) {
                return;
            }
            return originalNavigationSubscribe.call(this);
        };
    }

    // Batch HangoutState events during initialization
    const originalEmitChange = hangoutState._emitChange;
    const batchedEvents = [];
    let emitBatchTimeout = null;

    hangoutState._emitChange = function (eventType, data) {
        if (suppressNavigationEvents && (
            eventType === HANGOUT_EVENTS.BACKGROUND_CHANGED ||
            eventType === HANGOUT_EVENTS.STATE_CHANGED ||
            eventType === HANGOUT_EVENTS.SPOT_SELECTED
        )) {
            batchedEvents.push({ eventType, data });

            clearTimeout(emitBatchTimeout);
            emitBatchTimeout = setTimeout(() => {
                if (!suppressNavigationEvents) {
                    batchedEvents.forEach(event => {
                        // Ensure we don't emit STATE_CHANGED events with null data
                        if (event.eventType === HANGOUT_EVENTS.STATE_CHANGED && !event.data) {
                            console.warn('[initializeHangout] Skipping batched STATE_CHANGED event with null data');
                            return;
                        }
                        originalEmitChange.call(this, event.eventType, event.data);
                    });
                    batchedEvents.length = 0;
                }
            }, 300);
            return;
        }

        // Allow critical events immediately (like SESSION_STARTED)
        return originalEmitChange.call(this, eventType, data);
    };

    // Temporarily disable navigation rendering during initialization
    if ((/** @type {any} */ (window)).navigation) {
        (/** @type {any} */ (window)).navigation.render = function () {
            if (suppressNavigationEvents) {
                // Queue render for after initialization instead of ignoring completely
                clearTimeout(navigationRenderDebounce);
                navigationRenderDebounce = setTimeout(() => {
                    if (originalNavigationRender) {
                        originalNavigationRender.call((/** @type {any} */ (window)).navigation);
                    }
                }, 200);
                return;
            }

            clearTimeout(navigationRenderDebounce);
            navigationRenderDebounce = setTimeout(() => {
                if (originalNavigationRender) {
                    originalNavigationRender.call((/** @type {any} */ (window)).navigation);
                }
            }, 100);
        };
    }

    // // Track BackgroundService calls to identify performance issues
    const originalGetCurrentBackground = null; // window.BackgroundService?.getCurrentBackground;

    return {
        suppressNavigationEvents,
        setSuppressNavigationEvents: (value) => { suppressNavigationEvents = value; },
        originalEmitChange,
        originalNavigationSubscribe,
        originalNavigationRender,
        originalGetCurrentBackground,
        batchedEvents,
        emitBatchTimeout,
        navigationRenderDebounce
    };
};

const restorePerformanceOptimizations = (hangoutState, optimizations) => {
    const {
        setSuppressNavigationEvents,
        originalEmitChange,
        originalNavigationSubscribe,
        originalNavigationRender,
        originalGetCurrentBackground,
        batchedEvents,
        emitBatchTimeout,
        navigationRenderDebounce
    } = optimizations;

    setSuppressNavigationEvents(false);

    if (originalEmitChange) {
        hangoutState._emitChange = originalEmitChange;
        clearTimeout(emitBatchTimeout);

        // Emit any remaining batched events now that initialization is complete
        if (batchedEvents.length > 0) {
            batchedEvents.forEach(event => {
                // Ensure we don't emit STATE_CHANGED events with null data
                if (event.eventType === HANGOUT_EVENTS.STATE_CHANGED && !event.data) {
                    console.warn('[initializeHangout] Skipping STATE_CHANGED event with null data');
                    return;
                }
                originalEmitChange.call(hangoutState, event.eventType, event.data);
            });
            batchedEvents.length = 0;
        }
    }

    if ((/** @type {any} */ (window)).Navigation?.prototype && originalNavigationSubscribe) {
        (/** @type {any} */ (window)).Navigation.prototype._subscribeToHangoutEvents = originalNavigationSubscribe;
        if ((/** @type {any} */ (window)).navigation && (/** @type {any} */ (window)).navigation._subscribeToHangoutEvents) {
            (/** @type {any} */ (window)).navigation._subscribeToHangoutEvents();
        }
    }

    if ((/** @type {any} */ (window)).navigation && originalNavigationRender) {
        (/** @type {any} */ (window)).navigation.render = originalNavigationRender;
        setTimeout(() => {
            originalNavigationRender.call((/** @type {any} */ (window)).navigation);
        }, 50);
    }
};

const setupScenarioBackground = async (scenarioId) => {
    if (!scenarioId) return;

    await withGracefulFailure(() => sessionManager.handleRegularScenarioBackground(scenarioId), 'Setup scenario background');
};

const initializeCommonUI = async (hangoutState) => {
    await hangoutState.initIndexedDB();
    await initializeUI();
    setupEventListeners();
};

const setupScenarioDetails = async (hangoutState, scenarioId, prefetchedScenarioPromise = null) => {
    if (!scenarioId) {
        console.warn('setupScenarioDetails: No scenarioId provided');
        return;
    }

    await withNetworkRetry(async () => {
        const hangoutService = HangoutService.getInstance();

        // Check if this is a private scenario
        const currentConfig = await storageManager.getItem('currentHangoutConfig') || {};
        const isPrivateScenario = currentConfig.isPrivateScenario || false;

        let selectedScenario = null;
        const scenarioFetchStartedAt = performance.now();
        const scenarioSource = prefetchedScenarioPromise ? 'startup_prefetch' : 'hangout_service';
        try {
            selectedScenario = prefetchedScenarioPromise
                ? await prefetchedScenarioPromise
                : await hangoutService.getScenarioDetails(scenarioId);
        } catch (error) {
            if (isPrivateScenario) {
                console.error('setupScenarioDetails: Failed to get private scenario details:', error);
            } else {
                console.warn('setupScenarioDetails: Failed to get public scenario details:', error);
            }
            return;
        } finally {
            const fetchDurationMs = Math.round(performance.now() - scenarioFetchStartedAt);
        }

        if (selectedScenario) {
            await hangoutState.setSelectedScenario(selectedScenario);

            const hasExistingLocation = currentConfig.loc_id && currentConfig.loc_id !== '0';
            if (shouldSeedScenarioBackdrop(currentConfig)) {
                const resolved = resolveScenarioBackdrop(selectedScenario);
                if (resolved?.locId || resolved?.timeOfDay || resolved?.timeHour != null) {
                    await safeUpdateConfig({
                        ...(resolved.locId ? { loc_id: resolved.locId } : {}),
                        ...(resolved.timeOfDay ? { timeOfDay: resolved.timeOfDay } : {}),
                        ...(resolved.timeHour != null ? { timeHour: resolved.timeHour } : {})
                    });
                    if (resolved.timeHour != null) {
                        timeProgressionService.adoptScenarioHour(resolved.timeHour);
                    }
                    await refreshBackgroundAfterLocationChange();
                }
            } else if (!shouldPreserveLocalBackdrop(currentConfig) && !hasExistingLocation && (selectedScenario.loc_id || selectedScenario.loc)) {
                await safeUpdateConfig({
                    loc_id: selectedScenario.loc_id || selectedScenario.loc
                });
                await refreshBackgroundAfterLocationChange();
            }

            // TODO: here we might have an issue with group conversations and index ordering (we might swap goals by accident)
            // Merge scenario character goals into existing character config
            if (selectedScenario?.allCharacters && currentConfig.allCharacters) {
                const updatedCharacters = currentConfig.allCharacters.map((configChar, index) => {
                    // First try to match by character ID
                    let scenarioChar = selectedScenario.allCharacters.find(sc =>
                        sc.id.toString() === configChar.id.toString()
                    );

                    // If no exact ID match, use the scenario character at the same position
                    // This handles cases where user selected a different character than the scenario default
                    if (!scenarioChar && selectedScenario.allCharacters[index]) {
                        scenarioChar = selectedScenario.allCharacters[index];
                    }

                    // Only set goal from scenario if character doesn't already have a custom goal
                    if (scenarioChar?.goal && !configChar.goal) {
                        return {
                            ...configChar,
                            goal: scenarioChar.goal
                        };
                    } else if (configChar.goal) {
                    } else if (!scenarioChar?.goal) {
                    }

                    return configChar;
                });

                await safeUpdateConfig({
                    allCharacters: updatedCharacters
                });
            }
        } else {
            console.warn('setupScenarioDetails: No scenario details found for scenarioId:', scenarioId);
        }
    }, 'Setup scenario details');
};

const prefetchCharacterDetails = async (characterId, prefetchedCharacterPromise = null) => {
    if (!characterId) return null;

    try {
        if (prefetchedCharacterPromise) {
            return await prefetchedCharacterPromise;
        }
        const hangoutService = HangoutService.getInstance();
        return await hangoutService.getCharacterDetails(characterId);
    } catch (error) {
        console.warn(`[createCharacterObject] Failed to fetch character details from HangoutService for ${characterId}:`, error);
        return null;
    }
};

const mapAndStoreCharacterVoiceId = async (characterId, characterDetails) => {
    if (!characterId) return;

    try {
        const voiceIdPreferences = storage.get('characterVoiceIds') || {};
        if (voiceIdPreferences[characterId]) return;

        const normalizedGender = normalizeGender(characterDetails || {});
        const voiceSubDir = normalizedGender === 'male' ? 'male_voices' : 'voices';
        const availableVoices = await getAllVoices(voiceSubDir);
        if (!Array.isArray(availableVoices) || availableVoices.length === 0) return;

        let voiceToStore = null;

        if (characterDetails?.voice_id) {
            const jsonVoiceId = characterDetails.voice_id;
            const jsonVoiceIdString = String(jsonVoiceId);
            voiceToStore = availableVoices.find((voice) =>
                voice.provider_voice_id === jsonVoiceId ||
                String(voice.provider_voice_id) === jsonVoiceIdString ||
                voice.id === jsonVoiceId ||
                String(voice.id) === jsonVoiceIdString
            );

            if (!voiceToStore) {
                console.warn(`[createCharacterObject] Could not find voice mapping for provider_voice_id ${characterDetails.voice_id}`);
            }
        }

        if (!voiceToStore) {
            voiceToStore = pickDefaultElevenLabsVoice(availableVoices);
        }

        if (!voiceToStore) return;

        voiceIdPreferences[characterId] = voiceToStore.id;
        storage.set('characterVoiceIds', voiceIdPreferences);

        if (voiceToStore.provider) {
            const providerPrefs = storage.get('characterVoiceProviders') || {};
            providerPrefs[characterId] = voiceToStore.provider;
            storage.set('characterVoiceProviders', providerPrefs);
        }

    } catch (error) {
        console.warn(`[createCharacterObject] Failed to map voice_id for character ${characterId}:`, error);
    }
};

const createCharacterObject = async (config, index = 0, prefetchedCharacterDetails = null) => {
    const characterId = config.allCharacters?.[index]?.id;
    let characterName = config.allCharacters?.[index]?.name ?? 'Character';
    const outfitId = config.allCharacters?.[index]?.outfitId ?? 0;
    const goal = config.allCharacters?.[index]?.goal ?? config.goal ?? null;
    let bodytype = config.allCharacters?.[index]?.bodytype ?? config.allCharacters?.[index]?.bt ?? 603;
    let characterDetails = prefetchedCharacterDetails;

    if (!characterDetails) characterDetails = await prefetchCharacterDetails(characterId);

    if (characterDetails) {
        if (!characterName || characterName === 'Character' || characterName.startsWith('Character ')) {
            if (characterDetails?.name) {
                characterName = characterDetails.name;
            }
        }

        if (!bodytype || bodytype === 603) {
            bodytype = 603;
        }
    }

    await mapAndStoreCharacterVoiceId(characterId, characterDetails);
    const configCharacter = config.allCharacters?.[index] || {};

    return createHangoutCharacterData(
        { ...configCharacter, ...characterDetails },
        {
            id: characterId,
            name: characterName,
            outfitId: outfitId,
            bodytype: bodytype,
            goal: goal,
            isActive: true
        }
    );
};

const initializeCharacterSprite = async (character, index, isInitialSetup = true) => {
    await withGracefulFailure(async () => {
        const characterManagerModule = await import('./character-manager.js');
        const characterManager = characterManagerModule.characterManager;
        const storageManager = await characterManager._getStorageManager();
        const hangoutConfig = await storageManager.getItem('currentHangoutConfig');
        const isMultiCharacter = hangoutConfig?.allCharacters?.length > 1;

        // For multi-character: create indexed containers (.character-image-0, .character-image-1, etc.)
        // For single character: use existing .character-image container (don't create .character-image-0)
        if (isMultiCharacter) {
            let characterContainer = document.querySelector(`.character-image-${index}`);
            if (!characterContainer) {
                characterContainer = characterManager.createCharacterContainer(index, character.name, hangoutConfig);
            }
            if (hangoutConfig?.allCharacters?.[index]) {
                characterContainer.setAttribute('data-character-id', hangoutConfig.allCharacters[index].id);
            }
        } else {
            // Single character: bottom-left on desktop, centered on narrow viewports
            const singleContainer = /** @type {HTMLElement|null} */ (document.querySelector('.character-image:not([class*="character-image-"])'));
            if (singleContainer) {
                if (singleContainer.classList.contains('user-positioned')) {
                    // Keep a dragged sprite where the user left it.
                } else if (window.innerWidth >= 1025) {
                    singleContainer.style.removeProperty('left');
                    singleContainer.style.removeProperty('right');
                    singleContainer.style.removeProperty('transform');
                    singleContainer.style.setProperty('--character-transform', 'translateX(0)');
                } else {
                    singleContainer.style.setProperty('--character-transform', 'translateX(-50%)');
                    singleContainer.style.setProperty('transform', 'translateX(-50%)', 'important');
                    singleContainer.style.setProperty('left', '50%', 'important');
                    singleContainer.style.setProperty('right', 'auto', 'important');
                }
                (/** @type {any} */ (singleContainer))._positioningEnabled = true;
                if (hangoutConfig?.allCharacters?.[0]) {
                    singleContainer.setAttribute('data-character-id', hangoutConfig.allCharacters[0].id);
                }
            }
        }

        // Show the preview sprite from the modal immediately (avoids blank until AI responds)
        const previewSpriteUrl = toFullSizeSpriteUrl(
            hangoutConfig?.allCharacters?.[index]?.modalImage
        );
        if (previewSpriteUrl && previewSpriteUrl !== '/assets/images/kiss.webp') {
            try {
                const { updateCharacterImage } = await import('./character-manager.js');
                await updateCharacterImage(previewSpriteUrl, character.name || 'Character', index, null, false, false);
            } catch (e) {
                console.warn('Failed to apply preview sprite:', e);
            }
            return;
        }

        // Default behavior: do not load a sprite yet; wait for AI response emotion
    }, `Initialize character container for ${character.name}`);
};

const setupPrimaryCharacter = async (hangoutState, character) => {
    await hangoutState.setSelectedCharacter(character);
};

const setupOutfit = async (hangoutState, outfitId) => {
    if (!outfitId || outfitId === '0') return;

    await withGracefulFailure(() => hangoutState.setSelectedOutfit(outfitId), 'Setup character outfit');
};

const generateInitialGreeting = async (conversationId, scenarioId, characters, config) => {
    if (!scenarioId || !characters.length) return;

    await sessionManager.generateInitialGreeting(conversationId, scenarioId, characters, config);
};

const initializeMultiCharacterSession = async (hangoutState, config) => {
    await hangoutState.clearConversationStorageKeys();

    await hangoutState.setConversationId(null);
    clearHangoutStateMessages(hangoutState);

    // Reorder characters so selected character speaks first, others keep their scenario order
    // This matches the display order in the character modal and provides better UX
    const selectedCharacterId = config.characterId;
    let sortedCharacters;
    
    if (selectedCharacterId && config.allCharacters?.length > 1) {
        const selectedIndex = config.allCharacters.findIndex(c => String(c.id) === String(selectedCharacterId));
        if (selectedIndex > 0) {
            // Move selected character to front, keep others in original order
            const selected = config.allCharacters[selectedIndex];
            const others = config.allCharacters.filter((_, i) => i !== selectedIndex);
            sortedCharacters = [selected, ...others];
        } else {
            // Selected is already first or not found, keep original order
            sortedCharacters = [...config.allCharacters];
        }
    } else {
        // Fallback to original order if no selection
        sortedCharacters = [...config.allCharacters];
    }

    // Update config preserving all existing structure
    await safeUpdateConfig({
        allCharacters: sortedCharacters,
        conversationId: null
    });

    await initializeCommonUI(hangoutState);

    const startupPrefetch = hangoutState.consumeStartupPrefetch?.() || null;
    const scenarioDetailsPromise = setupScenarioDetails(
        hangoutState,
        config.scenarioId,
        startupPrefetch?.scenarioPromise || null
    );
    const uniqueCharacterIds = [...new Set(
        (sortedCharacters || [])
            .map((character) => character?.id)
            .filter((characterId) => characterId !== null && characterId !== undefined)
            .map(String)
    )];
    const characterDetailsEntriesPromise = Promise.all(uniqueCharacterIds.map(async (characterId) => {
        const prefetchedCharacterPromise = startupPrefetch?.characterPromisesById?.[characterId] || null;
        const details = await prefetchCharacterDetails(characterId, prefetchedCharacterPromise);
        return [characterId, details];
    }));
    const [scenarioSetupResult, characterDetailsResult] = await Promise.allSettled([
        scenarioDetailsPromise,
        characterDetailsEntriesPromise
    ]);
    if (scenarioSetupResult.status === 'rejected') {
        console.warn('initializeMultiCharacterSession: Failed to setup scenario details', scenarioSetupResult.reason);
    }
    const prefetchedCharacterDetailsById = new Map();
    if (characterDetailsResult.status === 'fulfilled') {
        characterDetailsResult.value.forEach((entry) => {
            const [characterId, details] = entry;
            prefetchedCharacterDetailsById.set(String(characterId), details);
        });
    }
    if (characterDetailsResult.status === 'rejected') {
        console.warn('initializeMultiCharacterSession: Failed to prefetch character details', characterDetailsResult.reason);
    }

    await setupScenarioBackground(config.scenarioId);
    await applyInitialBackground();

    const updatedConfig = await storageManager.getItem('currentHangoutConfig') || {};
    const charactersWithGoals = updatedConfig.allCharacters || sortedCharacters;
    const configWithGoals = {
        ...config,
        ...updatedConfig,
        allCharacters: charactersWithGoals
    };
    const enrichedCharacters = await Promise.all(charactersWithGoals.map((character, index) => {
        const characterId = String(character?.id ?? '');
        const prefetchedDetails = prefetchedCharacterDetailsById.get(characterId) || null;
        return createCharacterObject(configWithGoals, index, prefetchedDetails);
    }));

    await storageManager.setItem('currentHangoutConfig', {
        ...updatedConfig,
        allCharacters: enrichedCharacters
    });

    await hangoutState.setActiveCharacters(enrichedCharacters);
    await hangoutState.setCharacters(enrichedCharacters);
    const primaryCharacterIndex = config.primaryCharacterIndex ?? 0;
    const primaryOutfitId = enrichedCharacters?.[primaryCharacterIndex]?.outfitId ?? 0;
    await setupOutfit(hangoutState, primaryOutfitId);

    const primaryCharacter = enrichedCharacters[primaryCharacterIndex];
    if (primaryCharacter) {
        await setupPrimaryCharacter(hangoutState, primaryCharacter);
    } else {
        console.warn('initializeMultiCharacterSession: No primary character found in sorted characters');
    }

    for (let i = 0; i < enrichedCharacters.length; i++) {
        const character = enrichedCharacters[i];
        const characterWithOutfit = {
            ...character,
            outfitId: character.outfitId ?? 0
        };
        await initializeCharacterSprite(characterWithOutfit, i, false);
    }

    await generateInitialGreeting(null, config.scenarioId, enrichedCharacters, config);
};

const initializeSingleCharacterSession = async (hangoutState, config, isResuming = false) => {
    if (isResuming) {
        await initializeCommonUI(hangoutState);
        await initializeResumedVisuals();
        return;
    }

    await hangoutState.clearConversationStorageKeys();

    await hangoutState.setConversationId(null);
    clearHangoutStateMessages(hangoutState);

    // Update config preserving all existing structure
    await safeUpdateConfig({
        conversationId: null
    });

    await initializeCommonUI(hangoutState);

    const startupPrefetch = hangoutState.consumeStartupPrefetch?.() || null;
    const scenarioDetailsPromise = setupScenarioDetails(
        hangoutState,
        config.scenarioId,
        startupPrefetch?.scenarioPromise || null
    );
    const firstCharacterId = config.allCharacters?.[0]?.id;
    const prefetchedCharacterPromise = startupPrefetch?.characterPromisesById?.[String(firstCharacterId)] || null;
    const characterDetailsPromise = prefetchCharacterDetails(firstCharacterId, prefetchedCharacterPromise);
    const [scenarioSetupResult, characterDetailsResult] = await Promise.allSettled([
        scenarioDetailsPromise,
        characterDetailsPromise
    ]);
    if (scenarioSetupResult.status === 'rejected') {
        console.warn('initializeSingleCharacterSession: Failed to setup scenario details', scenarioSetupResult.reason);
    }

    await setupScenarioBackground(config.scenarioId);
    await applyInitialBackground();

    const updatedConfig = await storageManager.getItem('currentHangoutConfig') || {};
    const configWithGoals = { ...config, ...updatedConfig };

    const prefetchedCharacterDetails = characterDetailsResult.status === 'fulfilled'
        ? characterDetailsResult.value
        : null;
    if (characterDetailsResult.status === 'rejected') {
        console.warn('initializeSingleCharacterSession: Failed to prefetch character details', characterDetailsResult.reason);
    }
    const character = await createCharacterObject(configWithGoals, 0, prefetchedCharacterDetails);
    const previewModalImage = configWithGoals.allCharacters?.[0]?.modalImage;
    const singleCharacter = previewModalImage
        ? { ...character, modalImage: previewModalImage }
        : character;
    await hangoutState.setActiveCharacters([singleCharacter]);
    await hangoutState.setCharacters([singleCharacter]);
    await setupOutfit(hangoutState, singleCharacter.outfitId);
    await setupPrimaryCharacter(hangoutState, singleCharacter);
    await initializeCharacterSprite(singleCharacter, 0, false);

    await generateInitialGreeting(null, config.scenarioId, [singleCharacter], config);
};

const handleNewConversationFlow = async (hangoutState, config) => {
    // Global cleanup of audio/voice cache for new conversations
    const { cleanupAllAudioCache } = await import('./message-utils.js');
    cleanupAllAudioCache();
    const localHistoryCount = getGlobalHistoryMessages().length;
    const hasPersistedTranscript = localHistoryCount > 0;
    const isRefreshRecovery = config?.entrypoint === 'hangout_page_refresh'
        && hasPersistedTranscript;

    if (config?.isNewConversation && config.allCharacters?.length > 1 && !isRefreshRecovery) {
        await initializeMultiCharacterSession(hangoutState, config);
        return true;
    }

    if (config?.isNewConversation && config.allCharacters?.length > 1 && isRefreshRecovery) {
        await initializeCommonUI(hangoutState);
        await initializeResumedVisuals();
        return true;
    }

    if (config?.allCharacters?.[0]?.id && !(config.allCharacters?.length > 1)) {
        const optimizations = setupPerformanceOptimizations(hangoutState);
        const isResuming = (!config.isNewConversation && config.conversationId) || isRefreshRecovery;
        await initializeSingleCharacterSession(hangoutState, config, isResuming);
        restorePerformanceOptimizations(hangoutState, optimizations);
        return true;
    }

    if (config?.isNewConversation) {
        const optimizations = setupPerformanceOptimizations(hangoutState);
        await initializeSingleCharacterSession(hangoutState, config, false);
        restorePerformanceOptimizations(hangoutState, optimizations);
        return true;
    }

    return false;
};

const handleFallbackInitialization = async (hangoutState) => {
    await hangoutState.initIndexedDB();
    await initHangoutFromUrl();
    await initializeUI();
    setupEventListeners();
};

export async function initializeHangout() {
    if (hangoutInitializeStarted) return;
    hangoutInitializeStarted = true;
    try {
        initializeHangoutAnalytics();
        getAnalyticsTracker().trackSessionStart();

        await withGracefulFailure(async () => {
            await import('./conversation-queue.js');
        }, 'Initialize conversation queue early');

        const hangoutState = HangoutState.getInstance();
        await withGracefulFailure(() => hydrateUserResponseStyleFromDb(), 'Hydrate hangout response style');
        const currentSession = hangoutState.getCurrentSession();
        const config = await storageManager.getItem('currentHangoutConfig');

        if (config?.allCharacters?.length > 0) {
            const stateCharacters = hangoutState.getCharacters();
            if (!stateCharacters || stateCharacters.length === 0) {
                await hangoutState.setCharacters(config.allCharacters);
            }
        }

        isUserMessageSubmittedInSession = hasUserMessageInState(hangoutState.getMessages());
        await initializeLanguageControl(hangoutState);

        const resumeConversationId = currentSession?.conversationId || hangoutState.getConversationId() || config?.conversationId;
        const wantsResume = !!resumeConversationId && !config?.isNewConversation;
        if (wantsResume) {
            await withGracefulFailure(
                () => hydrateChatSummaryFromDb(resumeConversationId),
                'Hydrate hangout chat summary'
            );
            const resumeHandled = await dispatchResume(
                hangoutState,
                currentSession,
                config,
                initializeUI,
                setupEventListeners,
                setupScenarioDetails
            );
            if (resumeHandled) return;
            throw new Error(`Unable to resume conversation ${resumeConversationId}: Resume process failed`);
        }

        const newConversationHandled = await handleNewConversationFlow(hangoutState, config);
        if (newConversationHandled) return;

        await handleFallbackInitialization(hangoutState);

    } catch (error) {
        console.error('Critical initialization failure:', error);
        await handleInitializationFailure(error, 'Hangout initialization');
        throw error;
    }
}
